import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService, type AuthUser } from '@/services/authService';
import { tokenStore } from '@/services/apiService';
import { clearLocalStorage } from '@/utils/storage';
import { Sentry } from '@/utils/sentry';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Silently attempt to restore session from HTTP-only refresh cookie
async function restoreSession(): Promise<AuthUser | null> {
  const result = await authService.refresh();
  return result?.user ?? null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const initialized = useRef(false);
  const queryClient = useQueryClient();

  // On mount: try to restore session from refresh cookie (silent refresh)
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    restoreSession()
      .then((restoredUser) => {
        if (restoredUser) {
          setUser(restoredUser);
          queryClient.invalidateQueries({ queryKey: ['payouts'] });
        }
      })
      .catch((err) => {
        Sentry.captureException(err, { tags: { operation: 'restoreSession' }, level: 'warning' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [queryClient]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authService.login(email, password);
    setUser(result.user);
    queryClient.invalidateQueries({ queryKey: ['payouts'] });
  }, [queryClient]);

  const register = useCallback(async (email: string, password: string) => {
    const result = await authService.register(email, password);
    setUser(result.user);
    queryClient.invalidateQueries({ queryKey: ['payouts'] });
  }, [queryClient]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (err) {
      Sentry.captureException(err, { tags: { operation: 'logout' }, level: 'warning' });
    } finally {
      setUser(null);
      tokenStore.clear();
      clearLocalStorage();
      queryClient.clear();
    }
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

// Lightweight helper for use in storage.ts (outside React tree)
export function getAuthState(): { isAuthenticated: boolean; accessToken: string | null } {
  return {
    isAuthenticated: tokenStore.get() !== null,
    accessToken: tokenStore.get(),
  };
}
